import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { post, get, del } from "../../lib/request"

export const addMediaTag = createAsyncThunk("mediaTags/addTag", async ({ mediaId, tag }, { rejectWithValue }) => {
  try {
    // Send tag as query parameter per API specification
    const response = await post(`/media/${mediaId}/tag?tag=${encodeURIComponent(tag)}`, null)
    return { mediaId, tag: response }
  } catch (error) {
    return rejectWithValue(error.message || "Failed to add tag")
  }
})

export function mediaTagLookupIdsFromRow(mediaRow) {
  if (!mediaRow || typeof mediaRow !== "object") return []
  return [mediaRow.media?.id, mediaRow.mediaId, mediaRow.media_id, mediaRow.id, mediaRow.mediaAsset?.id].filter(
    (id) => id != null && String(id).trim() !== "",
  )
}

export function resolveMediaMetaTags(tagsByMedia, mediaRow) {
  if (!tagsByMedia) return []
  for (const id of mediaTagLookupIdsFromRow(mediaRow)) {
    const rows = tagsByMedia[id] || tagsByMedia[String(id)]
    if (Array.isArray(rows) && rows.length > 0) return dedupeMediaMetaTags(rows)
  }
  return []
}

function dedupeMediaMetaTags(rows) {
  const seen = new Set()
  const out = []
  for (const tag of rows) {
    const label = mediaMetaTagLabel(tag).toLowerCase()
    if (!label) continue
    const idKey = tag?.id != null ? `id:${tag.id}` : null
    if (seen.has(label) || (idKey && seen.has(idKey))) continue
    seen.add(label)
    if (idKey) seen.add(idKey)
    out.push(tag)
  }
  return out
}

export function mediaMetaTagLabel(tag) {
  if (tag == null) return ""
  if (typeof tag === "string") return tag.trim()
  return String(tag.description || tag.tag || tag.name || "").trim()
}

function normalizeMediaTagsResponse(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

export const getMediaTags = createAsyncThunk("mediaTags/getTags", async (mediaIds, { rejectWithValue }) => {
  try {
    if (!mediaIds || mediaIds.length === 0) {
      return []
    }

    const queryString = mediaIds.map((id) => `mediaIds=${id}`).join("&")
    const endpoint = `/media/tags?${queryString}`

    const response = await get(endpoint)
    return normalizeMediaTagsResponse(response)
  } catch (error) {
    return rejectWithValue(error.message || "Failed to fetch tags")
  }
})

// Delete a tag by tag ID
export const deleteMediaTag = createAsyncThunk(
  "mediaTags/deleteTag",
  async ({ tagId, mediaId }, { rejectWithValue }) => {
    try {
      await del(`/media/tags/${tagId}`)
      return { tagId, mediaId }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete tag")
    }
  },
)

const mediaTagsSlice = createSlice({
  name: "mediaTags",
  initialState: {
    // Store tags by mediaId: { [mediaId]: [{ id, tag, mediaId }, ...] }
    tagsByMedia: {},
    status: "idle", // idle | loading | succeeded | failed
    error: null,
  },
  reducers: {
    resetTagsState: (state) => {
      state.tagsByMedia = {}
      state.status = "idle"
      state.error = null
    },
    clearTagsForMedia: (state, action) => {
      const mediaId = action.payload
      delete state.tagsByMedia[mediaId]
    },
  },
  extraReducers: (builder) => {
    builder
      // Add tag
      .addCase(addMediaTag.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(addMediaTag.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { mediaId, tag } = action.payload
        if (!state.tagsByMedia[mediaId]) {
          state.tagsByMedia[mediaId] = []
        }
        // Add the new tag to the media's tag list
        state.tagsByMedia[mediaId].push(tag)
      })
      .addCase(addMediaTag.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })

      // Get tags
      .addCase(getMediaTags.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(getMediaTags.fulfilled, (state, action) => {
        state.status = "succeeded"
        const tags = normalizeMediaTagsResponse(action.payload)
        const groupedTags = {}

        tags.forEach((tag) => {
          const mediaId = tag?.media?.id ?? tag?.mediaId ?? tag?.media_id
          if (mediaId == null) return
          const key = String(mediaId)
          if (!groupedTags[key]) groupedTags[key] = []
          groupedTags[key].push({
            id: tag.id,
            description: tag.description ?? tag.tag ?? "",
            mediaId,
          })
        })
        Object.keys(groupedTags).forEach((key) => {
          groupedTags[key] = dedupeMediaMetaTags(groupedTags[key])
        })

        state.tagsByMedia = { ...state.tagsByMedia, ...groupedTags }
      })
      .addCase(getMediaTags.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })

      // Delete tag
      .addCase(deleteMediaTag.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(deleteMediaTag.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { tagId, mediaId } = action.payload

        // Remove the tag from the media's tag list
        if (state.tagsByMedia[mediaId]) {
          state.tagsByMedia[mediaId] = state.tagsByMedia[mediaId].filter((tag) => tag.id !== tagId)
        }
      })
      .addCase(deleteMediaTag.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const { resetTagsState, clearTagsForMedia } = mediaTagsSlice.actions
export default mediaTagsSlice.reducer
